import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Building2, Briefcase, Mic2, ArrowLeft, KeyRound, Copy, Upload, Users, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Shared vocab — kept consistent with Prime Supplier onboarding chips.
const INDUSTRY_VOCAB = [
  "Construction", "Engineering", "Architecture", "Electrical", "Mechanical",
  "HVAC", "Security", "Risk Management", "IT", "Cybersecurity",
  "Staffing", "Marketing", "Furniture", "Printing", "Promotional Products",
  "Facilities Management", "Janitorial", "Landscaping", "Food Services",
  "Transportation", "Logistics", "Professional Services", "Consulting",
  "Financial Services", "Legal", "Other",
];

// Federally-recognized diversity certifications (self-attested).
const CERT_VOCAB = [
  "MBE — Minority Business Enterprise",
  "WBE — Woman Business Enterprise",
  "DBE — Disadvantaged Business Enterprise",
  "SDVOSB — Service-Disabled Veteran-Owned Small Business",
  "VOSB — Veteran-Owned Small Business",
  "HUBZone",
  "8(a) Business Development",
];

// Summit Guest types — kept in sync with backend SUMMIT_GUEST_TYPES.
const GUEST_TYPES = [
  "Community Partner", "Invited Company", "Media",
  "Event Support", "Volunteer", "Other Invited Guest",
];

/** One reusable registration form for SBE / T+L Attendee / Speaker / Summit Guest. */
export default function ParticipantRegistration({ type }) {
  const nav = useNavigate();
  const [state, setState] = useState({
    // shared
    name: "", email: "", phone: "", company: "", title: "",
    // SBE (expanded)
    website: "", description: "", products_services: "",
    industries_served: [], matchmaking_preferences: [], certifications: [],
    capability_statement_file_id: null, capability_uploading: false,
    matchmaking_opt_in: true,
    // T+L
    department: "", procurement_area: "",
    // Speaker
    bio: "", session_title: "",
    // Summit Guest
    first_name: "", last_name: "", guest_type: "",
  });
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [accessCode, setAccessCode] = useState("");
  const [regClosed, setRegClosed] = useState(false);

  useEffect(() => {
    api.get("/event").then(({ data }) => {
      setAccessCode(data.access_code || "");
      setRegClosed(data.registration_open === false);
    }).catch(() => {});
  }, []);

  const meta = {
    sbe:         { icon: Building2, label: "Small Business Enterprise", back: "/register", subtitle: "No payment. Complete your capability profile so Prime Suppliers can discover you during Business Matchmaking." },
    tl_attendee: { icon: Briefcase, label: "Travel + Leisure Team",  back: "/register", subtitle: "Registration for Travel + Leisure associates, procurement leaders, sourcing professionals, and invited executives." },
    speaker:     { icon: Mic2,      label: "Speaker",                     back: "/register", subtitle: "No payment. Register your speaker profile and session details." },
    summit_guest: { icon: Users,    label: "Summit Guest",                back: "/register", subtitle: "For Community Partners, invited companies, media, event support, and other invited guests of the Summit. No payment required." },
  }[type];

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const toggle = (k, v) => setState((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const uploadCapability = async (file) => {
    if (!file) return;
    set("capability_uploading", true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "sbe_capability_statement");
      const { data } = await api.post("/public-uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("capability_statement_file_id", data.file_id || data.id);
      toast.success("Capability statement uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Capability statement upload failed.");
    } finally { set("capability_uploading", false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const fullName = type === "summit_guest"
      ? `${state.first_name.trim()} ${state.last_name.trim()}`.trim()
      : state.name.trim();
    if (!fullName || !state.email.trim()) return toast.error("Name and email are required.");
    if (type === "summit_guest") {
      if (!state.first_name.trim() || !state.last_name.trim()) return toast.error("First and last name are required.");
      if (!state.company.trim()) return toast.error("Company / Organization is required.");
      if (!state.guest_type) return toast.error("Please select a Guest Type.");
    }
    if (type === "sbe" && !state.company.trim()) return toast.error("Company name is required.");
    if (type === "sbe" && (state.description || "").length > 500) return toast.error("Company Description must be 500 characters or fewer.");
    setSaving(true);
    try {
      const payload = { type, name: fullName, email: state.email.trim() };
      if (state.phone) payload.phone = state.phone;
      if (state.title) payload.title = state.title;
      if (state.company) payload.company = state.company.trim();
      if (type === "sbe") {
        Object.assign(payload, {
          website: state.website.trim() || null,
          description: state.description.trim() || null,
          products_services: state.products_services.trim() || null,
          industries_served: state.industries_served,
          matchmaking_preferences: state.matchmaking_preferences,
          certifications: state.certifications,
          capability_statement_file_id: state.capability_statement_file_id || null,
          matchmaking_opt_in: true,
        });
      } else if (type === "tl_attendee") {
        Object.assign(payload, { department: state.department || null, procurement_area: state.procurement_area || null });
      } else if (type === "speaker") {
        Object.assign(payload, { bio: state.bio || null, session_title: state.session_title || null });
      } else if (type === "summit_guest") {
        payload.guest_type = state.guest_type;
      }
      const { data } = await api.post("/attendees/register", payload);
      setConfirmed({ id: data.participant.id, check_in_id: data.check_in_id, email: payload.email, name: payload.name });
      toast.success("Registration confirmed.");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : "Could not register.";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (regClosed && !confirmed) return (
    <Layout>
      <section className="container-summit py-16 md:py-24">
        <div className="max-w-xl" data-testid="registration-closed">
          <Clock size={28} className="text-[#A39171]" strokeWidth={1.4} />
          <div className="overline mt-6">{meta.label}</div>
          <h1 className="serif text-4xl sm:text-5xl mt-3">Registration is closed.</h1>
          <p className="mt-4 text-zinc-600 leading-relaxed">
            Registration for the Travel + Leisure Strategic Supplier Summit closed on
            <span className="text-black"> Thursday, September 10, 2026 at 3:00 PM ET</span>.
            Thank you for your interest — for assistance, please contact{" "}
            <a href="mailto:info@displayamerica.com" className="underline underline-offset-4">info@displayamerica.com</a>.
          </p>
          <div className="mt-8 grid gap-3">
            <Link to="/login" className="btn-black h-11 text-xs" data-testid="registration-closed-signin">
              Already registered? Sign in <ArrowRight size={14} />
            </Link>
            <Link to="/" className="btn-outline-black h-11 text-xs" data-testid="registration-closed-home">
              Return to Home <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );

  if (confirmed) return (
    <Layout>
      <section className="container-summit py-16 md:py-24">
        <div className="max-w-xl" data-testid="registration-confirmed">
          <CheckCircle2 size={28} className="text-emerald-600" strokeWidth={1.4} />
          <div className="overline mt-6">{meta.label}</div>
          <h1 className="serif text-4xl sm:text-5xl mt-3">Registration confirmed.</h1>
          <p className="mt-4 text-zinc-600 leading-relaxed">
            Thank you, {confirmed.name}. A confirmation email has been sent to <span className="text-black">{confirmed.email}</span>.
            The Display America team will follow up with any additional logistics before the Summit.
          </p>

          {accessCode && (
            <div className="mt-8 bg-[#0A0A0A] text-white p-6 md:p-7" data-testid="registration-access-code-card">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#C6B99A]">
                <KeyRound size={12} /> Summit Portal Access
              </div>
              <p className="mt-3 text-sm text-white/75 leading-relaxed">
                Use this access code any time you sign in from the Attendee tab to view Event Documents,
                the Agenda, and the Summit Concierge.
              </p>
              <div className="mt-4 flex items-center gap-3 border border-white/15 bg-black/40 px-4 py-3">
                <span className="text-[10px] uppercase tracking-[0.24em] text-white/40">Access Code</span>
                <span className="font-mono text-lg tracking-[0.14em]" data-testid="registration-access-code">{accessCode}</span>
                <button type="button"
                  onClick={() => { navigator.clipboard.writeText(accessCode); toast.success("Access code copied"); }}
                  className="ml-auto text-[11px] uppercase tracking-[0.2em] text-white/60 hover:text-white inline-flex items-center gap-1"
                  data-testid="registration-copy-code">
                  <Copy size={12} /> Copy
                </button>
              </div>
              <p className="mt-3 text-xs text-white/50">
                This code is also included in your confirmation email — keep it handy for later.
              </p>
            </div>
          )}

          <div className="mt-8 grid gap-3">
            <Link to={`/login?tab=attendee&code=${encodeURIComponent(accessCode)}`}
                  className="btn-black h-11 text-xs" data-testid="registration-signin">
              Sign in to the Summit Portal <ArrowRight size={14} />
            </Link>
            <Link to="/documents" className="btn-outline-black h-11 text-xs" data-testid="registration-view-documents">
              View Event Documents <ArrowRight size={14} />
            </Link>
            <Link to="/schedule" className="text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-black">
              View Event Schedule
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );

  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-12 md:py-14">
          <Link to={meta.back} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white">
            <ArrowLeft size={12} /> Back to registration types
          </Link>
          <div className="mt-6 flex items-center gap-3">
            <meta.icon size={20} className="text-[#A39171]" strokeWidth={1.4} />
            <span className="overline text-white/60">{meta.label}</span>
          </div>
          <h1 className="serif text-4xl sm:text-5xl mt-3">Event Registration</h1>
          <p className="mt-3 max-w-xl text-white/70">{meta.subtitle}</p>
        </div>
      </section>

      <section className="container-summit py-12 md:py-16">
        <form onSubmit={submit} className="max-w-2xl grid gap-6" data-testid={`register-form-${type}`}>
          {type !== "summit_guest" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline">{type === "sbe" ? "Primary Contact *" : "Full Name *"}</Label>
              <Input required value={state.name} onChange={(e) => set("name", e.target.value)}
                data-testid="reg-name" className="h-12 rounded-none mt-2" />
            </div>
            <div>
              <Label className="overline">Title{type === "sbe" || type === "tl_attendee" ? " *" : ""}</Label>
              <Input value={state.title} onChange={(e) => set("title", e.target.value)}
                required={type === "tl_attendee" || type === "sbe"}
                data-testid="reg-title" className="h-12 rounded-none mt-2" />
            </div>
            <div>
              <Label className="overline">Email *</Label>
              <Input required type="email" value={state.email} onChange={(e) => set("email", e.target.value)}
                data-testid="reg-email" className="h-12 rounded-none mt-2" />
            </div>
            <div>
              <Label className="overline">Phone{type === "sbe" ? " *" : ""}</Label>
              <Input value={state.phone} onChange={(e) => set("phone", e.target.value)}
                required={type === "sbe"}
                data-testid="reg-phone" className="h-12 rounded-none mt-2" />
            </div>
          </div>
          )}

          {type === "summit_guest" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline">First Name *</Label>
                <Input required value={state.first_name} onChange={(e) => set("first_name", e.target.value)}
                  data-testid="reg-first-name" className="h-12 rounded-none mt-2" />
              </div>
              <div>
                <Label className="overline">Last Name *</Label>
                <Input required value={state.last_name} onChange={(e) => set("last_name", e.target.value)}
                  data-testid="reg-last-name" className="h-12 rounded-none mt-2" />
              </div>
              <div>
                <Label className="overline">Title *</Label>
                <Input required value={state.title} onChange={(e) => set("title", e.target.value)}
                  data-testid="reg-title" className="h-12 rounded-none mt-2" />
              </div>
              <div>
                <Label className="overline">Company / Organization *</Label>
                <Input required value={state.company} onChange={(e) => set("company", e.target.value)}
                  data-testid="reg-company" className="h-12 rounded-none mt-2" />
              </div>
              <div>
                <Label className="overline">Email *</Label>
                <Input required type="email" value={state.email} onChange={(e) => set("email", e.target.value)}
                  data-testid="reg-email" className="h-12 rounded-none mt-2" />
              </div>
              <div>
                <Label className="overline">Mobile Phone <span className="text-zinc-400 tracking-normal normal-case">(optional)</span></Label>
                <Input value={state.phone} onChange={(e) => set("phone", e.target.value)}
                  data-testid="reg-phone" className="h-12 rounded-none mt-2" />
              </div>
              <div className="sm:col-span-2">
                <Label className="overline">Guest Type *</Label>
                <Select value={state.guest_type} onValueChange={(v) => set("guest_type", v)}>
                  <SelectTrigger className="h-12 rounded-none mt-2" data-testid="reg-guest-type">
                    <SelectValue placeholder="Select your guest type" />
                  </SelectTrigger>
                  <SelectContent>
                    {GUEST_TYPES.map((g) => (
                      <SelectItem key={g} value={g} data-testid={`reg-guest-type-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {type === "sbe" && (
            <>
              <div className="pt-2 pb-1 border-t border-zinc-200 mt-2">
                <div className="overline text-zinc-500">Company Profile</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Company Name *</Label>
                  <Input required value={state.company} onChange={(e) => set("company", e.target.value)}
                    data-testid="reg-company" className="h-12 rounded-none mt-2" />
                </div>
                <div>
                  <Label className="overline">Company Website</Label>
                  <Input type="url" value={state.website} onChange={(e) => set("website", e.target.value)}
                    placeholder="https://example.com" data-testid="reg-website"
                    className="h-12 rounded-none mt-2" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="overline flex justify-between">
                    <span>Company Description</span>
                    <span className="text-[10px] normal-case tracking-normal text-zinc-500">{(state.description || "").length}/500</span>
                  </Label>
                  <Textarea rows={3} value={state.description}
                    onChange={(e) => set("description", e.target.value.slice(0, 500))}
                    placeholder="Briefly describe your company — mission, differentiators, geographic footprint."
                    data-testid="reg-description" className="rounded-none mt-2" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="overline">Products & Services</Label>
                  <Textarea rows={3} value={state.products_services}
                    onChange={(e) => set("products_services", e.target.value)}
                    placeholder="What do you sell? List the products or services you would like Prime Suppliers to know about."
                    data-testid="reg-products-services" className="rounded-none mt-2" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="overline">Capability Statement</Label>
                  <label className="mt-2 flex items-center gap-3 px-4 h-12 border border-zinc-300 hover:border-black cursor-pointer text-sm text-zinc-600"
                         data-testid="reg-capability-label">
                    <Upload size={14} />
                    {state.capability_uploading
                      ? "Uploading…"
                      : state.capability_statement_file_id
                        ? "Capability statement received · Replace"
                        : "Upload capability statement (PDF preferred, DOCX or image also accepted)"}
                    <input type="file" hidden
                           accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                           data-testid="reg-capability"
                           onChange={(e) => uploadCapability(e.target.files?.[0])} />
                  </label>
                  <p className="mt-2 text-[11px] text-zinc-500 leading-relaxed">
                    A short capability statement (typically one page) helps Prime Suppliers evaluate you during Business Matchmaking. Optional but strongly recommended.
                  </p>
                </div>
              </div>

              <div className="pt-4 pb-1 border-t border-zinc-200 mt-2">
                <div className="overline text-zinc-500">Business Matchmaking</div>
                <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                  Select the industries you serve and the sourcing categories you would like to pursue with Prime Suppliers.
                  Prime Suppliers use these selections to identify potential subcontractors during Business Matchmaking.
                </p>
              </div>

              <div>
                <Label className="overline">Industries Served</Label>
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="reg-industries-served">
                  {INDUSTRY_VOCAB.map((v) => {
                    const on = state.industries_served.includes(v);
                    return (
                      <button key={v} type="button" onClick={() => toggle("industries_served", v)}
                        className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] border ${on ? "bg-black text-white border-black" : "bg-white border-zinc-300 hover:border-black"}`}>
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="overline">Business Matchmaking Interests</Label>
                <p className="text-xs text-zinc-500 mt-1">Categories you want to sell into during Business Matchmaking.</p>
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="reg-matchmaking-preferences">
                  {INDUSTRY_VOCAB.map((v) => {
                    const on = state.matchmaking_preferences.includes(v);
                    return (
                      <button key={v} type="button" onClick={() => toggle("matchmaking_preferences", v)}
                        className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] border ${on ? "bg-[#A39171] text-white border-[#A39171]" : "bg-white border-zinc-300 hover:border-[#A39171]"}`}>
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="overline">Business Certifications <span className="text-zinc-400 tracking-normal normal-case">(optional · self-attested)</span></Label>
                <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="reg-certifications">
                  {CERT_VOCAB.map((v) => {
                    const on = state.certifications.includes(v);
                    return (
                      <label key={v}
                        className={`flex items-start gap-2 p-2.5 border cursor-pointer text-xs ${on ? "border-black bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                        <input type="checkbox" checked={on} onChange={() => toggle("certifications", v)}
                               className="mt-0.5" />
                        <span>{v}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {type === "tl_attendee" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="overline">Department *</Label>
                <Input required value={state.department} onChange={(e) => set("department", e.target.value)}
                  data-testid="reg-department" className="h-12 rounded-none mt-2" />
              </div>
              <div>
                <Label className="overline">Procurement / Business Area</Label>
                <Input value={state.procurement_area} onChange={(e) => set("procurement_area", e.target.value)}
                  data-testid="reg-procurement-area" className="h-12 rounded-none mt-2" />
              </div>
            </div>
          )}

          {type === "speaker" && (
            <>
              <div>
                <Label className="overline">Company</Label>
                <Input value={state.company} onChange={(e) => set("company", e.target.value)}
                  data-testid="reg-company" className="h-12 rounded-none mt-2" />
              </div>
              <div>
                <Label className="overline">Session Assignment (optional)</Label>
                <Input value={state.session_title} onChange={(e) => set("session_title", e.target.value)}
                  data-testid="reg-session" className="h-12 rounded-none mt-2" />
              </div>
              <div>
                <Label className="overline">Bio (optional)</Label>
                <Textarea rows={4} value={state.bio} onChange={(e) => set("bio", e.target.value)}
                  data-testid="reg-bio" className="rounded-none mt-2" />
              </div>
            </>
          )}

          <div className="flex justify-end pt-2">
            <button type="submit" disabled={saving} className="btn-black h-12 px-8" data-testid="reg-submit">
              {saving ? "Registering…" : "Complete Registration"} <ArrowRight size={14} />
            </button>
          </div>
          <p className="text-xs text-zinc-500">
            A confirmation email will be sent from Display America to the address above.
          </p>
        </form>
      </section>
    </Layout>
  );
}
